import React, { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import { User, TagDefinition } from '../../../types';
import { useAuth } from '../../auth/contexts/AuthContext';
import { StarService } from '../../stars/services/StarService';
import StarGivingModal from '../../stars/components/StarGivingModal';
import { DISMISSAL_META, getNextDismissalType, resolveStudentDismissal } from '../utils/dismissalProcedure';
import '../../muses/styles/MuseTiers.css';

interface AttendanceGridsProps {
  students: User[];
  attendance: Record<string, boolean>; // studentId -> active/inactive
  onStudentClick?: (student: User) => void;
  onStudentPatched?: (studentId: string, patch: Partial<User>) => void;
  squadId?: string;
  isTeacher?: boolean;
  onAddStudent?: () => void;
}

const AttendanceGrids: React.FC<AttendanceGridsProps> = ({
  students,
  attendance,
  onStudentClick,
  onStudentPatched,
  squadId,
  isTeacher = false,
  onAddStudent
}) => {
  const { currentUser } = useAuth();
  const [editingStudentId, setEditingStudentId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [selectedStudent, setSelectedStudent] = useState<User | null>(null);
  const [isStarModalOpen, setIsStarModalOpen] = useState(false);
  const [studentLessonStars, setStudentLessonStars] = useState<Record<string, { starsReceived: number; canReceive: boolean }>>({});
  const [parentConsents, setParentConsents] = useState<Record<string, boolean | null>>({});
  const [tagDefinitions, setTagDefinitions] = useState<TagDefinition[]>([]);
  const [savingDismissalByStudent, setSavingDismissalByStudent] = useState<Record<string, boolean>>({});
  const [dismissalTooltip, setDismissalTooltip] = useState<{ text: string; x: number; y: number } | null>(null);

  // Load star information and parent consents for all students if teacher
  useEffect(() => {
    if (isTeacher && squadId && students.length > 0) {
      loadStudentStarInfo();
      loadParentConsents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTeacher, squadId, students]);

  // Load tag definitions so we can resolve equipped tag IDs to names
  useEffect(() => {
    let isMounted = true;

    const loadTagDefinitions = async () => {
      try {
        const snap = await getDocs(collection(db, 'tagDefinitions'));
        if (!isMounted) return;
        const defs = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as TagDefinition[];
        setTagDefinitions(defs);
      } catch (error) {
        console.error('Error loading tag definitions for attendance grids:', error);
      }
    };

    // Only bother loading tags if there are students present
    if (students.length > 0) {
      loadTagDefinitions();
    }

    return () => {
      isMounted = false;
    };
  }, [students.length]);

  const loadStudentStarInfo = async () => {
    if (!squadId) return;

    const starInfo: Record<string, { starsReceived: number; canReceive: boolean }> = {};

    for (const student of students) {
      try {
        const canReceiveInfo = await StarService.canReceiveMoreStars(student.id, squadId);
        starInfo[student.id] = {
          starsReceived: canReceiveInfo.starsReceived,
          canReceive: canReceiveInfo.canReceive
        };
      } catch (error) {
        console.error(`Error loading star info for student ${student.id}:`, error);
        starInfo[student.id] = { starsReceived: 0, canReceive: true };
      }
    }

    setStudentLessonStars(starInfo);
  };

  const loadParentConsents = async () => {
    const consents: Record<string, boolean | null> = {};

    for (const student of students) {
      try {
        // Check if student has parentIds array
        if (student.parentIds && student.parentIds.length > 0) {
          // Fetch first parent's consent data
          const parentId = student.parentIds[0];
          const parentRef = doc(db, 'users', parentId);
          const parentSnap = await getDoc(parentRef);

          if (parentSnap.exists()) {
            const parentData = parentSnap.data() as User;
            const photoRelease = parentData.consents?.photoRelease;
            // Check if consent was accepted (either accepted=true or value='accept')
            const photoConsent = photoRelease?.accepted === true || photoRelease?.value === 'accept';
            const hasResponse = photoRelease?.accepted !== undefined || photoRelease?.value !== undefined;
            consents[student.id] = hasResponse ? photoConsent : null;
          } else {
            consents[student.id] = null;
          }
        } else {
          consents[student.id] = null;
        }
      } catch (error) {
        console.error(`Error loading parent consent for student ${student.id}:`, error);
        consents[student.id] = null;
      }
    }

    setParentConsents(consents);
  };

  const handleStarClick = (student: User, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedStudent(student);
    setIsStarModalOpen(true);
  };

  const handleStarAwarded = (success: boolean, message: string) => {
    if (success) {
      // Reload star information to reflect the update
      loadStudentStarInfo();
    }
  };

  const renderStarSection = (student: User) => {
    if (!isTeacher || !squadId) return null;

    const starInfo = studentLessonStars[student.id] || { starsReceived: 0, canReceive: true };
    const isOnline = attendance[student.id] !== false;

    // Only show star section for online students
    if (!isOnline) return null;

    return (
      <div className="star-section-mini">
        <div className="star-slots-mini">
          {[...Array(3)].map((_, index) => {
            const isEarned = index < starInfo.starsReceived;
            const isClickable = !isEarned && starInfo.canReceive;

            return (
              <button
                key={index}
                className={`star-slot-mini ${isEarned ? 'earned' : ''} ${isClickable ? 'clickable' : ''}`}
                onClick={isClickable ? (e) => handleStarClick(student, e) : undefined}
                disabled={!isClickable}
                title={
                  isEarned
                    ? 'Star earned'
                    : isClickable
                    ? 'Click to award star'
                    : 'Maximum stars reached for this lesson'
                }
              >
                ⭐
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const handleNameClick = (e: React.MouseEvent, student: User) => {
    e.stopPropagation(); // Prevent the onStudentClick for the whole card from firing
    setEditingStudentId(student.id);
    setEditingName(student.displayName || '');
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingName(e.target.value);
  };

  const handleNameUpdate = async (studentId: string) => {
    if (!editingName.trim()) {
      // Maybe show an error, for now just revert
      setEditingStudentId(null);
      return;
    }

    const studentRef = doc(db, 'users', studentId);
    try {
      await updateDoc(studentRef, {
        displayName: editingName.trim()
      });
      onStudentPatched?.(studentId, { displayName: editingName.trim() });
    } catch (error) {
      console.error('Error updating student name:', error);
      // Optionally show an error message to the user
    } finally {
      setEditingStudentId(null);
    }
  };

  const handleDismissalIconClick = async (student: User, event: React.MouseEvent) => {
    event.stopPropagation();

    const studentId = String(student.id || '').trim();
    if (!studentId) return;
    if (savingDismissalByStudent[studentId]) return;

    const resolved = resolveStudentDismissal(student);
    const nextType = getNextDismissalType(resolved.type);

    setSavingDismissalByStudent((prev) => ({ ...prev, [studentId]: true }));
    try {
      const patch: Partial<User> = {
        dismissalProcedureType: nextType === 'unassigned' ? null : nextType,
        dismissalProcedureNote: nextType === 'special'
          ? (resolved.otherText || (student as any)?.dismissalProcedureNote || null)
          : null,
      };
      await updateDoc(doc(db, 'users', studentId), patch as any);
      onStudentPatched?.(studentId, patch);
    } catch (error) {
      console.error(`Error updating dismissal procedure for student ${studentId}:`, error);
    } finally {
      setSavingDismissalByStudent((prev) => ({ ...prev, [studentId]: false }));
    }
  };

  const showDismissalTooltip = (text: string, target: HTMLElement) => {
    const rect = target.getBoundingClientRect();
    const x = Math.min(rect.right, window.innerWidth - 16);
    const y = Math.min(rect.bottom + 8, window.innerHeight - 16);
    setDismissalTooltip({ text, x, y });
  };

  const hideDismissalTooltip = () => {
    setDismissalTooltip(null);
  };

  // Separate students into active and inactive
  const activeStudents = students.filter(student => attendance[student.id] !== false);
  const inactiveStudents = students.filter(student => attendance[student.id] === false);

  const getStudentDisplayTagName = (student: User): string => {
    if (!student.equippedTagId) {
      return 'Beginner Musician';
    }
    const tag = tagDefinitions.find(t => t.id === student.equippedTagId);
    return tag?.name || 'Beginner Musician';
  };

  const renderStudentGrid = (studentList: User[], className: string, emptyMessage: string) => (
    <div className={`attendance-grid ${className}`}>
      <div className="attendance-grid-header">
        <h4 className="attendance-grid-title">
          {className === 'todays-squad' ? "Today's Squad" : 'Absent'}
        </h4>
        <span className={`attendance-count ${className === 'sleeping-squad' ? 'sleeping-count' : ''}`}>
          {studentList.length}
        </span>
        {className === 'todays-squad' && onAddStudent && (
          <button type="button" className="roster-add-btn" onClick={onAddStudent}>＋ Add student</button>
        )}
      </div>
      
      <div className="attendance-grid-content">
        {studentList.length === 0 ? (
          <div className="empty-grid-message">{emptyMessage}</div>
        ) : (
          studentList.map(student => (
            <div
              key={student.id}
              className={`student-grid-item ${student.selectedMuse?.tier ? `student-card-tier-${student.selectedMuse.tier}` : ''}`}
              onClick={() => onStudentClick && onStudentClick(student)}
            >
              {/* Photo Consent Camera Icon: show neutral camera if consent true; else no icon */}
              {(() => {
                const photoConsent = parentConsents[student.id];
                if (photoConsent === true) {
                  return (
                    <div className="photo-consent-icon neutral" title="Photo consent on file">
                      📷
                    </div>
                  );
                }
                return null;
              })()}

              {(() => {
                const resolvedDismissal = resolveStudentDismissal(student);
                const iconMeta = DISMISSAL_META[resolvedDismissal.type];
                const isSaving = savingDismissalByStudent[student.id] === true;
                return (
                  <button
                    type="button"
                    className={`dismissal-procedure-icon ${isSaving ? 'saving' : ''}`}
                    onClick={(e) => { void handleDismissalIconClick(student, e); }}
                    onMouseEnter={(e) => showDismissalTooltip(`Dismissal: ${resolvedDismissal.tooltip.replace(/\.$/, '')}. Click to change.`, e.currentTarget)}
                    onMouseMove={(e) => showDismissalTooltip(`Dismissal: ${resolvedDismissal.tooltip.replace(/\.$/, '')}. Click to change.`, e.currentTarget)}
                    onMouseLeave={hideDismissalTooltip}
                    onFocus={(e) => showDismissalTooltip(`Dismissal: ${resolvedDismissal.tooltip.replace(/\.$/, '')}. Click to change.`, e.currentTarget)}
                    onBlur={hideDismissalTooltip}
                    aria-label={`Dismissal procedure: ${iconMeta.shortLabel}. Click to change.`}
                    disabled={isSaving}
                  >
                    <span>{iconMeta.emoji}</span>
                  </button>
                );
              })()}

              <div className="student-avatar-container">
                {student.selectedMuse && student.selectedMuse.img && (
                  <img
                    src={student.selectedMuse.img}
                    alt={student.selectedMuse.name}
                    className={`student-grid-avatar ${student.selectedMuse.tier ? `muse-tier-${student.selectedMuse.tier}` : ''}`}
                  />
                )}
                {className === 'sleeping-squad' && (
                  <div className="sleeping-indicator"></div>
                )}
              </div>
              <div className="student-info-section">
                {editingStudentId === student.id ? (
                  <input
                    type="text"
                    value={editingName}
                    onChange={handleNameChange}
                    onBlur={() => handleNameUpdate(student.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleNameUpdate(student.id);
                      if (e.key === 'Escape') setEditingStudentId(null);
                    }}
                    autoFocus
                    className="student-name-input"
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <span className="student-grid-name" onClick={(e) => handleNameClick(e, student)}>
                    {student.displayName}
                  </span>
                )}
                <div className="student-og-tag">
                  {`OG ${student.ogLevel ?? 0} | ${getStudentDisplayTagName(student)}`}
                </div>
                {renderStarSection(student)}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="attendance-grids">
      {renderStudentGrid(activeStudents, 'todays-squad', 'No active students')}
      {renderStudentGrid(inactiveStudents, 'sleeping-squad', 'All students are present!')}
      {dismissalTooltip && (
        <div
          className="dismissal-floating-tooltip"
          role="tooltip"
          style={{ left: dismissalTooltip.x, top: dismissalTooltip.y }}
        >
          {dismissalTooltip.text}
        </div>
      )}

      {/* Star Giving Modal */}
      {selectedStudent && currentUser && (
        <StarGivingModal
          isOpen={isStarModalOpen}
          onClose={() => {
            setIsStarModalOpen(false);
            setSelectedStudent(null);
          }}
          student={selectedStudent}
          teacherId={currentUser.uid}
          squadId={squadId!}
          onStarAwarded={handleStarAwarded}
        />
      )}
    </div>
  );
};

export default AttendanceGrids;
